export enum DESIGNER {
    /**
     * @value Add to navigation
     * @description Prompt to add something to the main navigation.
     */
    ADD_TO_NAVIGATION = 'NAVIGATION.ADD_TO_NAVIGATION',
    /**
     * @value Add to navigation (published content only)
     * @description Prompt to add a content to the main navigation and explaining that only published content can be added
     */
    ADD_TO_NAVIGATION_TOOLTIP = 'NAVIGATION.ADD_PUBLISHED_CONTENT',
    /**
     * @value Content class
     */
    CONTENT_CLASS = 'CONTENT_SETTINGS_CONTENT_CLASS',
    /**
     * @value Content language
     */
    CONTENT_LANGUAGE = 'DESIGNER.CONTENT_LANGUAGE',
    /**
     * @value Archived on %DATE%
     * @description The text display to inform when the content has been archived
     */
    CONTENT_STATUS_ARCHIVED_ON_DATE = 'DESIGNER.CONTENT_STATUS_ARCHIVED_ON_DATE',
    /**
     * @value Advanced editing: full control of all settings, layout, and style options.
     */
    DESIGNER_MODE_DESCRIPTION = 'SIMPLE_DESIGNER_MODE.EXPERT_MODE_DESCRIPTION',
    /**
     * @value You are currently working on this content in %LANG%. Switch to work in another language.
     * @description Text display in a tooltip on a content edition to warn the user of his current edition language.
     */
    EDITION_LANGUAGE_WARNING = 'DESIGNER.EDITION_LANGUAGE_WARNING',
    /**
     * @value Edit scheduling date
     * @description The label of the button to edit a content scheduling date
     */
    EDIT_SCHEDULING = 'DESIGNER.EDIT_SCHEDULING',
    /**
     * @value The expiration date cannot be set before the scheduled date
     * @description Text used as an error message warning the user that he has set a content expiration date before the publication date
     */
    EXPIRATION_DATE_BEFORE_SCHEDULE_ERROR = 'DESIGNER.EXPIRATION_DATE_BEFORE_SCHEDULE_ERROR',
    /**
     * @value Expiration date must only come after the current date and time
     */
    EXPIRATION_DATE_DATE_TOO_OLD_ERROR = 'CONTENT_ADMIN.EXPIRATION_DATE_DATE_TOO_OLD_ERROR',
    /**
     * @value The content will be automatically unpublished and will not be visible to readers anymore from the date you set up below.
     * @description Helper text to explain what the expiration date of a content do
     */
    EXPIRATION_DATE_HELPER = 'DESIGNER.EXPIRATION_DATE_HELPER',
    /**
     * @value Featured image alternative text
     * @description Label of the content field to set an alternative text for a featured image
     */
    FEATURED_IMAGE_ALTERNATIVE_TEXT = 'DESIGNER.FEATURED_IMAGE_ALTERNATIVE_TEXT',
    /**
     * @value A publication end date is mandatory for this content.
     */
    MANDATORY_END_DATE_HELPER = 'CONTENT_SETTINGS_MANDATORY_END_DATE_HELP',
    /**
     * @value Promotion
     * @description Title of the settings section to set promotion of a content.
     */
    PROMOTION_SETTINGS_SECTION_TITLE = 'DESIGNER.PROMOTION_SETTINGS_SECTION_TITLE',
    /**
     * @value The content will be automatically published and visible to readers from the date you set up below.
     * @description Helper text to explain what the publication date of a content do
     */
    PUBLICATION_DATE_HELPER = 'DESIGNER.PUBLICATION_DATE_HELPER',
    /**
     * @value Publish changes
     * @description Label of the button used to update the current article with the latest modifications
     */
    PUBLISH_CHANGES_LABEL = 'FRONT.ARTICLES.DIALOG.PUBLISH_CHANGES',
    /**
     * @value Publish now
     * @description The label of the button to publish immediately a scheduled content.
     */
    PUBLISH_NOW_LABEL = 'DESIGNER.PUBLISH_NOW_LABEL',
    /**
     * @value Reader
     * @description The label of the current designer mode when Reader is selected.
     * It's a short for the SIMPLE_DESIGNER_MODE.READER_MODE_LABEL key
     */
    READER_MODE_LABEL = 'SIMPLE_DESIGNER_MODE.READER_MODE_BUTTON_LABEL',
    /**
     * @value Featured until
     * @description The label of the featured end date field on the content publish summary dialog
     */
    RECAP_FEATURED_END_DATE_LABEL = 'DESIGNER.RECAP_FEATURED_END_DATE_LABEL',
    /**
     * @value Featured from
     * @description The label of the featured start date field on the content publish summary dialog
     */
    RECAP_FEATURED_START_DATE_LABEL = 'DESIGNER.RECAP_FEATURED_START_DATE_LABEL',
    /**
     * @value You may want to last check content settings prior publishing. Click Settings to edit information.
     * @description Message displayed on the recap dialog of a content summarizing all the changes made to the content, and used to warn the user to check his content settings.
     */
    RECAP_PUBLISH_MESSAGE = 'DESIGNER.RECAP_PUBLISH_MESSAGE',
    /**
     * @value Publish now?
     * @description Title of the recap dialog of a content that will be published now
     */
    RECAP_PUBLISH_TITLE = 'DESIGNER.RECAP_PUBLISH_TITLE',
    /**
     * @value You may want to last check content settings prior scheduling. Click Settings to edit information.
     */
    RECAP_SCHEDULE_MESSAGE = 'DESIGNER.RECAP_SCHEDULE_MESSAGE',
    /**
     * @value Schedule?
     * @description Title of the recap dialog of a content
     */
    RECAP_SCHEDULE_TITLE = 'DESIGNER.RECAP_SCHEDULE_TITLE',
    /**
     * @value Removed as homepage
     */
    REMOVED_AS_HOMEPAGE = 'DESIGNER.REMOVED_AS_HOMEPAGE',
    /**
     * @value Request revision
     * @description The label of the button to reject a content publication request.
     */
    REQUEST_REVISION_LABEL = 'DESIGNER.REFUSE_CONTENT_PUBLICATION_LABEL',
    /**
     * @value This content remains a draft for now.
     * @description A message explaining to the user that the content remains as draft because the review was rejected.
     */
    REVISION_REQUESTED_HELPER_MESSAGE = 'DESIGNER.REFUSED_HELPER_MESSAGE',
    /**
     * @value Set as homepage
     * @description Field label to set a content as a site homepage
     */
    SET_AS_HOMEPAGE = 'DESIGNER.SET_AS_HOMEPAGE',
    /**
     * @value Set as template
     */
    SET_AS_TEMPLATE = 'CONTENT_SET_TO_TEMPLATE',
    /**
     * @value Set expiration date
     * @description Text used generally on a button allowing the user to define an expiration date on his content
     */
    SET_EXPIRATION_DATE = 'DESIGNER.SET_EXPIRATION_DATE',
    /**
     * @value Show version history
     * @description The label of the button to access version history of a content being edited within the content editor.
     */
    SHOW_VERSION_HISTORY_LABEL = 'DESIGNER.SHOW_VERSION_HISTORY_LABEL',
    /**
     * @value Simple mode
     * @description Legacy contribution-mode dropdown: title for the Simple mode option (formerly labeled Writer mode).
     */
    SIMPLE_MODE_LABEL = 'SIMPLE_DESIGNER_MODE.SIMPLE_MODE_LABEL',
    /**
     * @value Unpublish
     */
    UNPUBLISH = 'CONTENT_SET_TO_DRAFT',
    /**
     * @value Saved changes not published
     * @description A message displayed in the designer to warn the user that the last saved version is not published yet.
     */
    UNPUBLISHED_SAVED_CHANGES_WARNING_MESSAGE = 'DESIGNER.UNPUBLISHED_SAVED_CHANGES_WARNING_MESSAGE',
    /**
     * @value Unschedule
     * @description Action to unschedule a video visible by the user when the status of the video is "scheduled"
     */
    UNSCHEDULE = 'PLAY.VIDEO_STATUS_UNSCHEDULE',
    /**
     * @value Content unscheduled
     */
    UNSCHEDULE_SUCCESS_MESSAGE = 'DESIGNER.UNSCHEDULE_SUCCESS_MESSAGE',
    /**
     * @value Basic editing: write content and manage basic widget settings.
     */
    WRITER_MODE_DESCRIPTION = 'SIMPLE_DESIGNER_MODE.WRITER_MODE_DESCRIPTION',
    /**
     * @value Writer
     * @description The label of the current designer mode when Writer mode is selected.
     * It's a short for the SIMPLE_DESIGNER_MODE.WRITER_MODE_LABEL key.
     */
    WRITER_MODE_LABEL = 'SIMPLE_DESIGNER_MODE.WRITER_MODE_BUTTON_LABEL',
}
